import { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, TrendingUp, Clock, BadgeCheck } from 'lucide-react';
import { TrustScoreRing, TrustBadgeComponent, TrustBreakdownModal } from '../features/trust-score';
import type { TrustScore, TrustBadge } from '../features/trust-score';

// Get color based on key
const getBarColor = (key: string): string => {
  switch(key) {
    case 'identity':
      return 'linear-gradient(90deg, #3b6fff, #599aff)';
    case 'reliability':
      return 'linear-gradient(90deg, #10b981, #34d399)';
    case 'activity':
      return 'linear-gradient(90deg, #f59e0b, #fbbf24)';
    case 'responsiveness':
      return 'linear-gradient(90deg, #ec4899, #f472b6)';
    default:
      return '';
  }
};
const demoTrustScore: TrustScore = {
  overall: 78,
  breakdown: {
    identity: 90,
    reliability: 75,
    activity: 70,
    responsiveness: 85,
  },
  level: 'Trusted',
  nextMilestone: {
    name: 'Expert Seller',
    pointsNeeded: 22,
  },
  badges: [
    {
      id: '1',
      type: 'identity',
      tier: 'gold',
      icon: 'identity',
      name: 'Verified Identity',
      description: 'Government ID verified',
      earnedAt: new Date('2024-01-15'),
      progress: 100,
      requirements: [],
    },
    {
      id: '2',
      type: 'contact',
      tier: 'silver',
      icon: 'contact',
      name: 'Phone Verified',
      description: 'Phone number confirmed',
      earnedAt: new Date('2024-01-10'),
      progress: 100,
      requirements: [],
    },
    {
      id: '3',
      type: 'transaction',
      tier: 'silver',
      icon: 'transaction',
      name: 'Quick Responder',
      description: 'Responds within 1 hour',
      earnedAt: new Date('2024-02-20'),
      progress: 100,
      requirements: [],
    },
    {
      id: '4',
      type: 'social',
      tier: 'bronze',
      icon: 'social',
      name: 'Social Connect',
      description: 'Connected social accounts',
      earnedAt: null,
      progress: 60,
      requirements: [
        { id: 'r1', description: 'Connect Facebook', completed: true },
        { id: 'r2', description: 'Connect Instagram', completed: false },
        { id: 'r3', description: 'Share a listing', completed: true },
      ],
    },
    {
      id: '5',
      type: 'transaction',
      tier: 'gold',
      icon: 'transaction',
      name: 'Top Seller',
      description: '50+ successful sales',
      earnedAt: null,
      progress: 72,
      requirements: [
        { id: 'r1', description: 'Complete 50 sales', completed: false },
        { id: 'r2', description: 'Maintain 4.5+ rating', completed: true },
      ],
    },
    {
      id: '6',
      type: 'premium',
      tier: 'platinum',
      icon: 'premium',
      name: 'Elite Member',
      description: 'Premium subscription active',
      earnedAt: new Date('2024-03-01'),
      progress: 100,
      requirements: [],
    },
  ],
};

export default function TrustScoreDemo(): JSX.Element {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [_selectedBadge, setSelectedBadge] = useState<TrustBadge | null>(null);

  return (
    <div className="min-h-screen bg-dark-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-dark-900 mb-4">
            Trust Score Visualization
          </h1>
          <p className="text-dark-500 text-lg">
            Gamified seller reputation system with animated score rings and badges
          </p>
        </motion.div>

        {/* Main Score Display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-xl p-8 mb-8"
        >
          <div className="flex flex-col items-center">
            <TrustScoreRing
              score={demoTrustScore.overall}
              size="xl"
              animated
              interactive
              onClick={() => setShowBreakdown(true)}
            />
            <h2 className="text-2xl font-bold text-dark-900 mt-6">
              {demoTrustScore.level} Seller
            </h2>
            <p className="text-dark-500 mt-2">
              {demoTrustScore.nextMilestone?.pointsNeeded} points to{' '}
              {demoTrustScore.nextMilestone?.name}
            </p>
            <button
              onClick={() => setShowBreakdown(true)}
              className="mt-4 text-primary-600 font-medium hover:text-primary-700"
            >
              View Details →
            </button>
          </div>
        </motion.div>

        {/* Badge Collection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-3xl shadow-xl p-8"
        >
          <h3 className="text-xl font-bold text-dark-900 mb-6">Badges</h3>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
            {demoTrustScore.badges.map((badge: TrustBadge, index: number) => (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="flex flex-col items-center"
              >
                <TrustBadgeComponent
                  badge={badge}
                  size="lg"
                  onClick={() => setSelectedBadge(badge)}
                />
                <span className="text-xs text-dark-500 mt-2 text-center">
                  {badge.name}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Breakdown Bars */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-3xl shadow-xl p-8 mt-8"
        >
          <h3 className="text-xl font-bold text-dark-900 mb-6">Score Breakdown</h3>
          <div className="space-y-4">
            {Object.entries(demoTrustScore.breakdown).map(([key, value], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="space-y-2"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    {key === 'identity' && <BadgeCheck className="w-4 h-4 text-primary-500" />}
                    {key === 'reliability' && <Shield className="w-4 h-4 text-success" />}
                    {key === 'activity' && <TrendingUp className="w-4 h-4 text-warning" />}
                    {key === 'responsiveness' && <Clock className="w-4 h-4 text-accent" />}
                    <span className="capitalize font-medium text-dark-700">{key}</span>
                  </div>
                  <span className="font-bold text-dark-900">{value}%</span>
                </div>
                <div className="h-3 bg-dark-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: getBarColor(key) }}
                    initial={{ width: 0 }}
                    animate={{ width: `${value}%` }}
                    transition={{ duration: 1, delay: 0.8 + index * 0.1 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Breakdown Modal */}
      <TrustBreakdownModal
        isOpen={showBreakdown}
        onClose={() => setShowBreakdown(false)}
        trustScore={demoTrustScore}
        onVerify={(_badgeId: string) => console.log('Verify badge:', _badgeId)}
      />
    </div>
  );
}
